import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import mutate from "@/Utils/request/mutate";
import { FacilityRead } from "@/types/facility/facility";
import facilityApi from "@/types/facility/facilityApi";

export default function FacilityPrintImageSettings({
  facility,
  canUpdate,
}: {
  facility: FacilityRead;
  canUpdate: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["facility", facility.id] });
  const upload = useMutation({
    mutationFn: mutate(facilityApi.uploadPrintImage, {
      pathParams: { facilityId: facility.id },
    }),
    onSuccess: () => {
      toast.success(t("hospital_print_image_updated"));
      void refresh();
    },
  });
  const remove = useMutation({
    mutationFn: mutate(facilityApi.deletePrintImage, {
      pathParams: { facilityId: facility.id },
    }),
    onSuccess: () => {
      toast.success(t("hospital_print_image_deleted"));
      void refresh();
    },
  });
  const pending = upload.isPending || remove.isPending;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("hospital_print_image")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">
          {t("hospital_print_image_hint")}
        </p>
        {facility.read_print_image_url && (
          <img
            src={facility.read_print_image_url}
            alt={t("hospital_print_image")}
            className="h-24 max-w-full object-contain"
          />
        )}
        {canUpdate && (
          <>
            <Label htmlFor="hospital-print-image">{t("upload")}</Label>
            <Input
              id="hospital-print-image"
              type="file"
              accept="image/png,image/jpeg"
              disabled={pending}
              onChange={async (event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = "";
                if (!file) return;
                if (
                  !["image/png", "image/jpeg"].includes(file.type) ||
                  file.size > 2 * 1024 * 1024
                ) {
                  toast.error(t("hospital_print_image_hint"));
                  return;
                }
                const body = new FormData();
                body.append("print_image", file);
                try {
                  await upload.mutateAsync(body);
                } catch {
                  /* The request handler displays the API error. */
                }
              }}
            />
            {facility.read_print_image_url && (
              <Button
                variant="outline"
                disabled={pending}
                onClick={() => remove.mutate()}
              >
                {t("remove")}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
